// src/components/Navbar.js
import React, { useState } from 'react';
import { AppBar, Toolbar, Button, Box, Typography, IconButton, Drawer, List, ListItem, ListItemText, Menu, MenuItem } from '@mui/material';
import { KeyboardArrowDown, Menu as MenuIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Logo from '../assets/logo1.png'; // Adjust the path as needed
import { styled } from '@mui/material/styles';
import { useMediaQuery, useTheme } from '@mui/material';
import products from '../pages/ProductData';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    backdropFilter: 'blur(18px)',
    boxShadow: '0 18px 45px rgba(16, 32, 51, 0.08)',
    color: theme.palette.text.primary,
    padding: '0 16px',
    position: 'sticky',
    top: 0,
    zIndex: 20,
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
}));

const LogoImage = styled('img')({
    height: '52px',
    marginRight: '16px',
    objectFit: 'contain',
});

const Motto = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '13px',
    fontWeight: 600,
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    '& .MuiDrawer-paper': {
        backgroundColor: theme.palette.background.paper,
        width: '250px',
        padding: '24px 16px',
    },
}));

const Navbar = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [productsAnchor, setProductsAnchor] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const toggleDrawer = (open) => (event) => {
        setDrawerOpen(open);
    };

    const openProducts = (event) => {
        setProductsAnchor(event.currentTarget);
    };

    const closeProducts = () => {
        setProductsAnchor(null);
    };

    const menuItems = (
        <List>
            <ListItem button component={Link} to="/" onClick={toggleDrawer(false)}>
                <ListItemText primary="Home" />
            </ListItem>
            <ListItem button component={Link} to="/about" onClick={toggleDrawer(false)}>
                <ListItemText primary="About Us" />
            </ListItem>
            <ListItem button component={Link} to="/services" onClick={toggleDrawer(false)}>
                <ListItemText primary="Services" />
            </ListItem>
            {products.map((product) => (
                <ListItem button component={Link} to={product.path} onClick={toggleDrawer(false)} key={product.name}>
                    <ListItemText primary={product.name} />
                </ListItem>
            ))}
            <ListItem button component="a" href={products[0].externalUrl} target="_blank" rel="noreferrer" onClick={toggleDrawer(false)}>
                <ListItemText primary="Visit Skylark Reads" />
            </ListItem>
            <ListItem button component={Link} to="/careers" onClick={toggleDrawer(false)}>
                <ListItemText primary="Careers" />
            </ListItem>
            <ListItem button component={Link} to="/contact" onClick={toggleDrawer(false)}>
                <ListItemText primary="Contact" />
            </ListItem>
        </List>
    );

    return (
        <StyledAppBar position="static">
            <Toolbar sx={{ minHeight: { xs: 72, md: 78 }, px: { xs: 0, md: 2 } }}>
                <LogoContainer sx={{ minWidth: 0 }}>
                    <Link to="/" style={{ textDecoration: 'none' }}>
                        <LogoImage src={Logo} alt="Company Logo" />
                    </Link>
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Typography variant="h6" component="div" color="primary">
                            Epytome IT
                        </Typography>
                        <Motto>
                            Crafting Tomorrow’s Solutions with Today’s Expertise 
                        </Motto>
                    </Box>
                </LogoContainer>
                
                {isMobile ? (
                    <>
                        <IconButton 
                            edge="end" 
                            color="inherit" 
                            onClick={toggleDrawer(true)} 
                            style={{ marginLeft: 'auto' }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <StyledDrawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
                            {menuItems}
                        </StyledDrawer>
                    </>
                ) : (
                    <Box ml="auto">
                        <Button component={Link} to="/" color="inherit">Home</Button>
                        <Button component={Link} to="/about" color="inherit">About Us</Button>
                        <Button component={Link} to="/services" color="inherit">Services</Button>
                        <Button color="inherit" endIcon={<KeyboardArrowDown />} onClick={openProducts}>Products</Button>
                        <Menu anchorEl={productsAnchor} open={Boolean(productsAnchor)} onClose={closeProducts}>
                            {products.map((product) => (
                                <MenuItem component={Link} to={product.path} onClick={closeProducts} key={product.name}>
                                    {product.name}
                                </MenuItem>
                            ))}
                            <MenuItem component="a" href={products[0].externalUrl} target="_blank" rel="noreferrer" onClick={closeProducts}>
                                Visit Skylark Reads
                            </MenuItem>
                        </Menu>
                        <Button component={Link} to="/careers" color="inherit">Careers</Button>
                        <Button component={Link} to="/contact" variant="contained" sx={{ ml: 1 }}>Contact</Button>
                    </Box>
                )}
            </Toolbar>
        </StyledAppBar>
    );
};

export default Navbar;
